<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="px-6 pt-6 pb-6">

      <Loader :visible="isLoading" :text="loadingText" />
      
      <div class="bg-white rounded-lg shadow-lg p-4">
        <!-- Filtro cliente sopra il calendario -->
        <div class="mb-4 flex items-center justify-between">
          <div class="flex items-center gap-4">
            <button 
              @click="openNewAppointmentModal"
              class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Nuovo appuntamento
            </button>
            <ClientSelectorModal
              v-if="!isMobile()"
              :clients-list="clientsList"
              :selected-client="selectedClient"
              @client-selected="selectClient"
            />
          </div>
          
          <div>
            <button 
              @click="goToMappa"
              class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Mappa
            </button>
          </div>
        </div>
        
        <FullCalendar
          ref="fullCalendar"
          :options="calendarOptions"
          class="fc-rounded"
        />
      </div>
    </div>

    <!-- Modal nuovo appuntamento -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative my-auto">
        <h3 class="text-lg font-semibold mb-4">
          {{ editingVisit ? 'Modifica Appuntamento' : 'Nuovo Appuntamento' }}
        </h3>
        <form @submit.prevent="editingVisit ? updateAppointment() : saveAppointment()" class="max-h-[70vh] overflow-y-auto">
          <!-- Toggle per scegliere tra Cliente+Sede e Contatto -->
          <div class="mb-4">
            <label class="block text-gray-700 mb-2">Seleziona indirizzo da:</label>
            <div class="flex space-x-4">
              <label class="flex items-center">
                <input type="radio" v-model="addressSourceType" value="cliente" class="mr-2" />
                Cliente
              </label>
              <label class="flex items-center">
                <input type="radio" v-model="addressSourceType" value="contatto" class="mr-2" />
                Contatto
              </label>
            </div>
          </div>
          
          <!-- Selezione Cliente (visibile solo se addressSourceType è 'sede') -->
          <div v-if="addressSourceType === 'cliente'" class="mb-4">
            <label class="block text-gray-700">Cliente</label>
            <v-select :options="foundClients" :filterable="false" v-model="newAppointment.company" label="name"
              @search="searchClients" @option:selected="onClientSelect" required>
              <template #no-options="{ search, searching }">
                <div v-if="searching">🔍 Ricerca in corso...</div>
                <div v-else-if="!search">Inizia a digitare per cercare un cliente...</div>
              </template>
            </v-select>
            <div v-if="newAppointment.errors.company" class="py-4 text-red-800">{{ newAppointment.errors.company }}</div>
          </div>
          
          <!-- Selezione Sede (visibile solo se addressSourceType è 'sede') -->
          <div v-if="addressSourceType === 'cliente'" class="mb-4">
            <label class="block text-gray-700">Sede</label>
            <select v-model="newAppointment.branch_id" class="p93__input" :disabled="!newAppointment.company" @change="setAddressFromSite">
              <option value="" :selected="newAppointment.branch_id === ''">Sede principale</option>
              <option v-for="site in sites" :key="site.id" :value="site.id">{{ site.name }}</option>
            </select>
            <div v-if="newAppointment.errors.branch_id" class="py-4 text-red-800">{{ newAppointment.errors.branch_id }}</div>
          </div>
          
          <!-- Selezione Contatto (visibile solo se addressSourceType è 'contatto') -->
          <div v-if="addressSourceType === 'contatto'" class="mb-4">
            <label class="block text-gray-700">Contatto</label>
            <v-select 
              :options="foundContacts" 
              :filterable="false" 
              v-model="newAppointment.contact" 
              label="name"
              @search="searchContacts" 
              @option:selected="onContactSelect" 
              required>
              <template #no-options="{ search, searching }">
                <div v-if="searching">🔍 Ricerca in corso...</div>
                <div v-else-if="!search">Inizia a digitare per cercare un contatto...</div>
              </template>
            </v-select>
            <div v-if="newAppointment.errors.contact" class="py-4 text-red-800">{{ newAppointment.errors.contact }}</div>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Indirizzo</label>
            <!-- Campo input normale quando si sta modificando e showAddressPicker è false -->
            <input
              v-show="editingVisit && !showAddressPicker"
              v-model="newAppointment.address"
              type="text"
              class="p93__input cursor-pointer"
              placeholder="Clicca per modificare l'indirizzo..."
              readonly
            />
            
            <!-- Input per Google Places Autocomplete -->
            <input
              v-show="(editingVisit && showAddressPicker) || !editingVisit"
              ref="addressInput"
              v-model="newAppointment.address"
              type="text"
              class="p93__input"
              placeholder="Inserisci indirizzo..."
              @blur="hideAddressPicker"
            />
            
            <div v-if="newAppointment.errors.address" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.address }}</div>
          </div>
          <!-- Campo selezione operatori se l'utente è admin/super admin -->
          <div v-if="users && users.length > 0" class="mb-4">
            <label class="block text-sm font-medium mb-2">Operatori assegnati</label>
            <div class="border border-gray-300 rounded-md p-3 bg-gray-50 max-h-40 overflow-y-auto">
              <div v-for="user in users" :key="user.id" class="flex items-center mb-2 last:mb-0">
                <input 
                  type="checkbox" 
                  :id="`operator-${user.id}`"
                  :value="user.id"
                  v-model="newAppointment.operator_ids"
                  class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label 
                  :for="`operator-${user.id}`" 
                  class="ml-2 text-sm text-gray-700 cursor-pointer select-none"
                >
                  {{ user.name }}
                </label>
              </div>
              <div v-if="users.length === 0" class="text-gray-500 text-sm">
                Nessun operatore disponibile
              </div>
            </div>
            <small class="text-gray-600 mt-1 block">Seleziona uno o più operatori per questo appuntamento</small>
            <div v-if="newAppointment.errors.operator_ids" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.operator_ids }}</div>
          </div>
          
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Data</label>
            <input
              type="date"
              v-model="newAppointment.date"
              class="p93__input"
              required
            />
            <div v-if="newAppointment.errors.date" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.date }}</div>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-sm font-medium mb-1">Ora Inizio</label>
              <input
                type="time"
                v-model="newAppointment.start_time"
                class="p93__input"
                required
              />
              <div v-if="newAppointment.errors.start_time" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.start_time }}</div>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">Ora Fine</label>
              <input
                type="time"
                v-model="newAppointment.end_time"
                class="p93__input"
                required
              />
              <div v-if="newAppointment.errors.end_time" class="text-red-500 text-sm pt-2">{{ newAppointment.errors.end_time }}</div>
            </div>
          </div>
          <div class="mb-6">
            <label class="block text-sm font-medium mb-1">Motivazione</label>
            <textarea
              v-model="newAppointment.description"
              class="p93__input"
              rows="2"
              placeholder="Motivazione della visita"
              required
            ></textarea>
          </div>
          
          <!-- Campo note di conclusione per visite già concluse -->
          <div v-if="editingVisit && editingVisit.concluded_at" class="mb-6">
            <label class="block text-sm font-medium mb-1">
              Note di conclusione 
              <span class="text-green-600 text-sm">
                (conclusa il {{ new Date(editingVisit.concluded_at).toLocaleString('it-IT') }})
              </span>
            </label>
            <textarea
              v-model="newAppointment.conclusion_notes"
              class="p93__input"
              rows="3"
              placeholder="Note sulla conclusione della visita..."
            ></textarea>
          </div>
          
          <!-- Campo note di conclusione per nuova conclusione -->
          <div v-if="showConcludeConfirmation" class="mb-6">
            <label class="block text-sm font-medium mb-1">Note di conclusione (opzionale)</label>
            <textarea
              v-model="conclusionNotes"
              class="p93__input"
              rows="3"
              placeholder="Inserisci eventuali note sulla conclusione della visita..."
            ></textarea>
          </div>

          <div v-if="editingVisit && uploadDMS && editingVisit.concluded_at">
            <label class="block text-sm font-medium mb-1">Allegati caricati</label>
            <div v-if="!attachmentLoaded">
              <span>Caricamento allegati...</span>
            </div>
            <div v-else>
              <ul class="list-disc list-inside mb-4">
                <li v-for="attachment in visitAttachments" :key="attachment.id" class="text-sm text-gray-700">
                  <a :href="route('modules.visite-commerciali.allegatoDMS', attachment.id)" target="_blank" class="text-blue-600 hover:underline">
                    {{ attachment.name }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="showConcludeConfirmation && uploadDMS" class="mb-6">
            <div
                    class="border-2 border-dashed rounded-2xl p-4 relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 hover:border-gray-400"
                    role="button"
                    tabindex="0"
                    @click="openFileDialog"
                    @keydown.enter.prevent="openFileDialog"
                    @keydown.space.prevent="openFileDialog"
                    @dragenter.prevent="onDragEnter"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop"
                    aria-describedby="filepicker-desc"
                  >
                    <input
                      ref="input"
                      type="file"
                      class="sr-only"
                      @change="onInputChange"
                      multiple="true"
                      aria-hidden="true"
                    />

                    <div class="flex items-center gap-3">
                      <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M12 3v12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 7l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M21 21H3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>

                      <div>
                        <p id="filepicker-desc" class="text-sm text-gray-700">Trascina qui i file che vuoi allegare oppure fai click per selezionarli.</p>
                      </div>
                    </div>

                    <div v-if="currentAttachments.length" class="mt-3 space-y-2">
                      <div v-for="f in currentAttachments" :key="f.name" class="flex items-center justify-between gap-2">
                        <div class="flex items-center gap-2">
                          <svg class="w-5 h-5 text-gray-500" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M4 7v8a2 2 0 0 0 2 2h8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M10 3v6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          <div>
                            <div class="text-sm font-medium text-gray-800">{{ f.name }}</div>
                            <div class="text-xs text-gray-500">{{ prettySize(f.size) }}</div>
                          </div>
                        </div>

                        <div class="flex items-center gap-2">
                          <button type="button" class="text-sm px-3 py-1 rounded-md border" @click.stop="removeFile(f)">Rimuovi</button>
                        </div>
                      </div>
                    </div>
                  </div>

          </div>
          
          <div class="flex justify-end gap-2">
            <template v-if="showDeleteConfirmation">
              <button type="button" @click="cancelDelete" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Annulla Eliminazione
              </button>
              <button type="button" @click="confirmDelete" class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                Conferma Eliminazione
              </button>
            </template>
            <template v-else-if="showConcludeConfirmation">
              <button type="button" @click="cancelConclude" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                Annulla
              </button>
              <button type="button" @click="confirmConclude" class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                Conferma Conclusione
              </button>
            </template>
            <template v-else>
              <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Annulla</button>
              <button 
                v-if="editingVisit && !editingVisit.concluded_at && ($page.props.isAdminOrSuperAdmin || this.editingVisit.user.id === $page.props.auth.user.id || this.editingVisit.operators.find(x => x.id == $page.props.auth.user.id))"
                type="button" 
                @click="showConcludeConfirmation = true" 
                class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Concludi
              </button>
              <button 
                v-if="editingVisit && ($page.props.isAdminOrSuperAdmin || this.editingVisit.user.id === $page.props.auth.user.id)"
                type="button" 
                @click="showDeleteConfirmation = true" 
                class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Elimina
              </button>
              <button
                v-if="(editingVisit && ($page.props.isAdminOrSuperAdmin || this.editingVisit.user.id === $page.props.auth.user.id || this.editingVisit.operators.find(x => x.id == $page.props.auth.user.id))) || !editingVisit"
                type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                {{ editingVisit ? 'Aggiorna' : 'Salva' }}
              </button>
            </template>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, useForm, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import ClientSelectorModal from '@/Components/ClientSelectorModal.vue';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import Loader from '@/Components/Loader.vue';

export default {
  props: ['errors', 'clientsList', 'users', 'uploadDMS'],
  components: {
    Link,
    BreadCrumbs,
    ClientSelectorModal,
    FullCalendar,
    Loader
  },
  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Visite commerciali', href: route('modules.home', 'visite-commerciali') },
        { name: 'Calendario', href: route('modules.visite-commerciali.calendario') }
      ],
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        initialView: 'listMonth',
        locale: itLocale,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: this.isMobile() ? '' : 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
        },
        views: {
          listAll: {
            type: 'list',
            visibleRange: () => ({
              start: '2000-01-01',
              end: new Date(new Date().getFullYear() + 2, 0, 1).toISOString().split('T')[0]
            }),
            buttonText: 'Tutti',
            titleFormat: () => ''
          }
        },
        buttonText: {
          today: 'Oggi',
          month: 'Mese',
          week: 'Settimana',
          day: 'Giorno',
          listMonth: 'Lista mensile',
          listAll: 'Tutti'
        },
        height: 'auto',
        allDaySlot: false,
        selectable: true,
        selectMirror: true,
        selectAllow: (selectInfo) => {
          // Permetti solo selezioni con durata massima di 24 ore
          const start = new Date(selectInfo.start);
          const end = new Date(selectInfo.end);
          const diffInHours = (end - start) / (1000 * 60 * 60); // Differenza in ore
          return diffInHours <= 24;
        },
        dayMaxEvents: true,
        weekends: true,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventDrop: this.handleEventDropOrResize,
        eventResize: this.handleEventDropOrResize,
        events: this.fetchEvents,
        editable: true,
        droppable: true,
        eventDidMount: this.handleEventMount, // Aggiungi per personalizzare il rendering
      },
      showModal: false,
      showClientSuggestions: false,
      clientSearchTerm: '',
      selectedClient: '',
      selectedDate: null,
      autocomplete: null,
      editingVisit: null,
      showDeleteConfirmation: false,
      showConcludeConfirmation: false,
      conclusionNotes: '',
      showAddressPicker: false, // Nuovo flag per controllare la visualizzazione del picker
      visitsData: [], // Memorizza localmente le visite caricate
      newAppointment: useForm({
        address: '',
        latitude: null,
        longitude: null,
        start_time: '',
        end_time: '',
        description: '',
        date: '',
        company: '',
        company_name: '',
        operator_ids: [], // Nuovo campo per gli operatori assegnati
        source: 'calendario',
        branch_id: '',
        contact: '', // Nuovo campo per il contatto selezionato
        contact_id: '', // ID del contatto selezionato
        conclusion_notes: '' // Campo per le note di conclusione
      }),
      foundClients: [],
      foundContacts: [], // Nuova proprietà per i contatti trovati
      sites: [],
      addressSourceType: 'cliente', // Determina se selezionare sede cliente o contatto
      currentAttachments: [],
      readfileramaining: -1,
      isLoading: false,
      loadingText: 'Caricamento...',
      visitAttachments: [],
      attachmentLoaded: false,
      pendingAppointmentId: null,
      pendingAppointment: null
    };
  },
  mounted() {
    const params = new URLSearchParams(window.location.search);
    const appointmentId = params.get('appointment');
    if (appointmentId) {
      this.pendingAppointmentId = appointmentId;
      axios.get(route('modules.visite-commerciali.appuntamento', { id: this.pendingAppointmentId }))
        .then(response => {
          const appt = response.data.data || response.data;
          this.pendingAppointment = appt;
          const calApi = this.$refs.fullCalendar?.getApi();
          if (calApi) {
            calApi.gotoDate(new Date(appt.start_time));
          }
          const alreadyLoaded = this.visitsData.find(v => v.id == appt.id);
          if (alreadyLoaded) {
            this.pendingAppointment = null;
            this.pendingAppointmentId = null;
            this.$nextTick(() => this.openVisitFromData(alreadyLoaded));
          }
        })
        .catch(() => {});
    }
  },
  beforeUnmount() {
    // Cleanup autocomplete
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  },
  watch: {
    selectedClient(newVal) {
      const calApi = this.$refs.fullCalendar?.getApi();
      if (calApi && !this.isMobile()) {
        const right = newVal
          ? 'dayGridMonth,timeGridWeek,timeGridDay,listMonth,listAll'
          : 'dayGridMonth,timeGridWeek,timeGridDay,listMonth';
        calApi.setOption('headerToolbar', { left: 'prev,next today', center: 'title', right });
        if (!newVal && calApi.view.type === 'listAll') {
          calApi.changeView('listMonth');
        }
      }
      this.refreshCalendar();
    },
    addressSourceType() {
      // Reset dei campi quando cambia il tipo di selezione indirizzo
      if (this.editingVisit) return;
      this.resetAddressSelection();
    }
  },
  methods: {
    isMobile() {
      return window.innerWidth <= 768; // Considera mobile se la larghezza è <= 768px
    },
    handleDateSelect(selectInfo) {
      this.selectedDate = selectInfo;
      
      // Estrai solo la data dal selectInfo.start
      const selectedDate = new Date(selectInfo.start);
      const dateString = selectedDate.getFullYear() + '-' + 
                        String(selectedDate.getMonth() + 1).padStart(2, '0') + '-' + 
                        String(selectedDate.getDate()).padStart(2, '0');
      
      this.newAppointment.date = dateString;
      
      // Usa l'orario selezionato dal calendario
      const startDate = new Date(selectInfo.start);
      const endDate = new Date(selectInfo.end);
      
      // Se è una selezione di giorno intero (senza orario specifico)
      if (selectInfo.allDay || startDate.getHours() === 0 && startDate.getMinutes() === 0 && endDate.getHours() === 0 && endDate.getMinutes() === 0) {
        this.newAppointment.start_time = '08:00';
        this.newAppointment.end_time = '09:00';
      } else {
        // Usa l'orario selezionato
        const startHours = startDate.getHours().toString().padStart(2, '0');
        const startMinutes = startDate.getMinutes().toString().padStart(2, '0');
        this.newAppointment.start_time = `${startHours}:${startMinutes}`;
        
        const endHours = endDate.getHours().toString().padStart(2, '0');
        const endMinutes = endDate.getMinutes().toString().padStart(2, '0');
        this.newAppointment.end_time = `${endHours}:${endMinutes}`;
      }
      
      this.showModal = true;
      this.$refs.fullCalendar.getApi().unselect();
      
      // Setup dell'autocomplete dopo che il modal è visibile
      this.$nextTick(() => {
        this.setupAutocompleteForModal();
      });
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    resetForm() {
      this.newAppointment.clearErrors();
      this.newAppointment.reset();
      this.selectedDate = null;
      this.editingVisit = null;
      this.showAddressPicker = false;
      this.showClientSuggestions = false;
      this.showDeleteConfirmation = false;
      this.showConcludeConfirmation = false;
      this.conclusionNotes = '';
      this.foundClients = [];
      this.foundContacts = [];
      this.sites = [];
      this.addressSourceType = 'cliente'; // Reset al valore predefinito
      this.visitAttachments = [];
      this.currentAttachments = [];
      this.attachmentLoaded = false;
    },
    resetAddressSelection() {
      // Reset dei campi quando cambia il tipo di selezione indirizzo
      this.newAppointment.address = '';
      this.newAppointment.latitude = null;
      this.newAppointment.longitude = null;
      this.newAppointment.company = '';
      this.newAppointment.company_name = '';
      this.newAppointment.branch_id = '';
      this.newAppointment.contact = '';
      this.newAppointment.contact_id = '';
      this.sites = [];
      this.foundClients = [];
      this.foundContacts = [];
    },
    saveAppointment() {
      this.newAppointment.post(route('modules.visite-commerciali.nuova'), {
        preserveScroll: true,
        onSuccess: () => {
          this.closeModal();
          this.resetForm();
          this.refreshCalendar();
        }
      });
    },
    updateAppointment() {
      this.newAppointment.post(route('modules.visite-commerciali.modifica', this.editingVisit.id), {
        preserveScroll: true,
        onSuccess: () => {
          this.closeModal();
          this.refreshCalendar();
        }
      });
    },
    deleteAppointment() {
      this.newAppointment.post(route('modules.visite-commerciali.elimina', this.editingVisit.id), {
        preserveScroll: true,
        onSuccess: () => {
          this.closeModal();
          this.refreshCalendar();
        }
      });
    },
    
    confirmDelete() {
      this.deleteAppointment();
    },
    
    cancelDelete() {
      this.showDeleteConfirmation = false;
    },
    showLoader(text = 'Caricamento...') {
      this.isLoading = true;
      this.loadingText = text;
    },
    hideLoader() {
      this.isLoading = false;
    },
    concludeAppointment(attachments = []) {

      this.showLoader('Registrazione in corso...');
      // Invio la richiesta per concludere la visita
      this.$inertia.post(route('modules.visite-commerciali.concludi', this.editingVisit.id), {
        conclusion_notes: this.conclusionNotes,
        source: 'calendario',
        attachments: attachments
      }, {
        preserveScroll: true,
        onSuccess: () => {
          this.currentAttachments = [];
          this.closeModal();
          this.refreshCalendar();
        },
        onFinish: () => {
          this.hideLoader();
        }
      });
    },
    
    confirmConclude() {
      if (this.currentAttachments.length > 0) {
        this.emitFiles();
      }
      else this.concludeAppointment();
    },
    
    cancelConclude() {
      this.showConcludeConfirmation = false;
      this.conclusionNotes = '';
    },
    
    closeClientModal() {
      this.showClientModal = false;
      this.clientSearchTerm = '';
    },
    
    selectClient(clientCompany) {
      this.selectedClient = clientCompany;
    },
    fetchEvents(fetchInfo, successCallback, failureCallback) {
      const startDate = fetchInfo.start.toISOString().split('T')[0];
      const endDate = fetchInfo.end.toISOString().split('T')[0];
      
      // Aggiungi il filtro cliente ai parametri
      const params = {
        start: startDate,
        end: endDate
      };
      
      if (this.selectedClient) {
        params.company = this.selectedClient;
      }
      
      axios.get(route('modules.visite-commerciali.visite'), { params })
        .then(response => {
          this.visitsData = response.data.data || response.data;

          if (this.pendingAppointment) {
            const appt = this.pendingAppointment;
            this.pendingAppointment = null;
            this.pendingAppointmentId = null;
            this.$nextTick(() => this.openVisitFromData(appt));
          }
          
          const events = this.visitsData.map(visit => {
            // Mostra gli operatori assegnati o il creatore se non ci sono operatori
            let operators = '';
            if (visit.operators && visit.operators.length > 0) {
              operators = visit.operators.map(op => op.name).join(', ');
            } else {
              operators = visit.creator?.name || 'N/A';
            }
            
            return {
              id: visit.id,
              title: `${operators} - ${visit.company_name || visit.contact?.name || 'N/A'}`,
              start: visit.start_time,
              end: visit.end_time,
              extendedProps: {
                description: visit.description,
                address: visit.address,
                operators: operators,
                creatorName: visit.creator?.name || 'N/A',
                concluded_at: visit.concluded_at,
                conclusion_notes: visit.conclusion_notes
              }
            };
          });
          
          successCallback(events);
        })
        .catch(error => {
          failureCallback(error);
        });
    },
    
    refreshCalendar() {
      const calendarApi = this.$refs.fullCalendar.getApi();
      calendarApi.refetchEvents();
    },
    
    loadGoogleMaps() {
      // Verifica se Google Maps è già caricato
      if (window.google && window.google.maps) {
        this.initAutocomplete();
        return;
      }

      // Carica Google Maps se non è già caricato
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey()}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.initAutocomplete();
      };
      document.head.appendChild(script);
    },

    initAutocomplete() {
      
      // Aspetta che il DOM sia pronto
      this.$nextTick(() => {
        const input = this.$refs.addressInput;
        if (input && window.google && window.google.maps) {
          this.autocomplete = new google.maps.places.Autocomplete(input, {
            types: ['address'],
            fields: ['formatted_address', 'geometry', 'name']
          });

          this.autocomplete.addListener('place_changed', () => {
            const place = this.autocomplete.getPlace();
            this.handlePlaceChange(place);
          });
        } else {
          setTimeout(() => {
            this.initAutocomplete();
          }, 500);
        }
      });
    },

    setupAutocompleteForModal() {
      setTimeout(() => {
        this.initAutocomplete();
      }, 100);
    },
    
    handlePlaceChange(place) {
      if (place && place.formatted_address) {
        this.newAppointment.address = place.formatted_address;
        
        // Estrai le coordinate se disponibili
        if (place.geometry && place.geometry.location) {
          this.newAppointment.latitude = place.geometry.location.lat();
          this.newAppointment.longitude = place.geometry.location.lng();
        }
        
        // Nascondi il picker dopo la selezione se siamo in modalità modifica
        if (this.editingVisit) {
          this.showAddressPicker = false;
        }
      } else {
      }
    },
    
    hideAddressPicker() {
      // Nascondi il picker solo se siamo in modalità modifica
      if (this.editingVisit) {
        this.showAddressPicker = true;
      }
    },
    openVisitFromData(visit) {
      if (!visit) return;
      this.handleEventClick({ event: { id: visit.id } });
    },
    handleEventClick(clickInfo) {
      const event = clickInfo.event;
      const visitId = event.id;
      
      const visit = this.visitsData.find(v => v.id == visitId);
      
      if (visit) {
        this.editingVisit = visit;
        this.showAddressPicker = false;
        
        // Precompila il form con i dati della visita esistente PRIMA di determinare il tipo
        this.newAppointment.address = visit.address || '';
        this.newAppointment.company = visit.company || '';
        this.newAppointment.description = visit.description || '';
        this.newAppointment.latitude = visit.latitude;
        this.newAppointment.longitude = visit.longitude;
        this.newAppointment.branch_id = visit.branch_id || '';
        this.newAppointment.company_name = visit.company?.name || '';
        this.newAppointment.contact = visit.contact || '';
        this.newAppointment.contact_id = visit.contact ? visit.contact.id : '';
        this.newAppointment.conclusion_notes = visit.conclusion_notes || '';

        // Precompila gli operatori assegnati se presenti
        this.newAppointment.operator_ids = visit.operators ? visit.operators.map(op => op.id) : [];

        // Estrai data e orari dal datetime
        const startDate = new Date(visit.start_time);
        const endDate = new Date(visit.end_time);
        
        this.newAppointment.date = startDate.toISOString().split('T')[0];
        this.newAppointment.start_time = startDate.toTimeString().slice(0, 5);
        this.newAppointment.end_time = endDate.toTimeString().slice(0, 5);
        
        // Determina il tipo di selezione indirizzo basato sui dati esistenti
        if (visit.company_name) {
          this.addressSourceType = 'cliente';
          // Precarica il cliente nell'array foundClients per farlo apparire nella select
          if (visit.company) {
            this.foundClients = [visit.company];
            this.onClientSelect(visit.company, false);
          }
        } else if (visit.contact && visit.contact.name) {
          this.addressSourceType = 'contatto';
          // Precarica il contatto nell'array foundContacts per farlo apparire nella select
          this.foundContacts = [visit.contact];
          this.newAppointment.contact = visit.contact;
          this.onContactSelect(visit.contact, false);
        }

        if (visit.branch_id) {
          this.newAppointment.branch_id = visit.branch_id;
          // Il setAddressFromSite verrà chiamato dopo che getSites avrà caricato i dati
        }

        if (this.uploadDMS){
          this.attachmentLoaded = false;
          this.loadAttachments();
        }
        
        this.showModal = true;
        
        // Setup dell'autocomplete dopo che il modal è visibile
        this.$nextTick(() => {
          this.setupAutocompleteForModal();
        });
      }
    },
    handleEventDropOrResize(info) {
      const event = info.event;
      const visitId = event.id;
      
      // Trova la visita corrispondente
      const visit = this.visitsData.find(v => v.id == visitId);
      
      if (visit) {
        this.editingVisit = visit;
        this.showAddressPicker = false;
        this.isClientAppointment = false; // Reset del flag cliente

        // Estrai le nuove date e orari
        const newStartDate = event.start;
        const newEndDate = event.end || new Date(newStartDate.getTime() + (60 * 60 * 1000));

        this.newAppointment.date = newStartDate.toISOString().split('T')[0];
        this.newAppointment.start_time = newStartDate.toTimeString().slice(0, 5);
        this.newAppointment.end_time = newEndDate.toTimeString().slice(0, 5);

        this.newAppointment.address = visit.address || '';
        this.newAppointment.company = visit.company || '';
        this.newAppointment.description = visit.description || '';
        this.newAppointment.latitude = visit.latitude;
        this.newAppointment.longitude = visit.longitude;
        this.newAppointment.user_id = visit.user.id || '';
        this.newAppointment.branch_id = visit.branch_id || '';

        this.updateAppointment();
      }
    },
    handleEventMount(info) {
      // Personalizza il rendering dell'evento
      const event = info.event;
      const element = info.el;

      if (event.title === '') {
        return;
      }

      // Se la visita è conclusa, aggiungere stile visivo distintivo
      if (event.extendedProps.concluded_at) {
        element.style.backgroundColor = '#10b981'; // Verde per le visite concluse
        element.style.borderColor = '#059669';
        element.classList.add('opacity-80');
      }

      // Aggiungi tooltip con descrizione completa
      element.setAttribute('title', 
        `${event.title}\n` +
        `Descrizione: ${event.extendedProps.description}\n` +
        `Indirizzo: ${event.extendedProps.address}\n` +
        `Operatori: ${event.extendedProps.operators}\n` +
        `Creato da: ${event.extendedProps.creatorName}`
      );
      
      // Oppure personalizza il contenuto HTML
      const titleElement = element.querySelector('.fc-event-title');
      if (titleElement) {
        titleElement.innerHTML = `
          <div class="font-bold">${event.title}</div>
          <div class="text-xs opacity-75">${event.extendedProps.description}</div>
        `;
      }
    },
    goToMappa() {
      router.visit(route('modules.visite-commerciali.mappa'));
    },
    openNewAppointmentModal() {
      this.resetForm();
      this.editingVisit = null;
      this.showModal = true;
      
      // Setup dell'autocomplete dopo che il modal è visibile
      this.$nextTick(() => {
        this.setupAutocompleteForModal();
      });
    },
    searchClients(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchContactsTimeout);
        this.searchContactsTimeout = setTimeout(() => {
          axios
            .get(route('modules.contratti.ricerca-clienti', search))
            .then((response) => {
              this.foundClients = response.data;
              loading(false);
            });
        }, 300);
      }
    },
    searchContacts(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchContactsTimeout);
        this.searchContactsTimeout = setTimeout(() => {
          axios
            .get(route('modules.visite-commerciali.ricerca-contatti', search))
            .then((response) => {
              this.foundContacts = response.data;
              loading(false);
            });
        }, 300);
      }
    },
    onClientSelect(selectedClient, shouldLoadSites = true) {
      this.newAppointment.company_name = selectedClient.name || '';
      if (shouldLoadSites) {
        this.getSites(selectedClient);
        this.setAddressFromClient(selectedClient);
      }
    },
    onContactSelect(selectedContact, shouldSetAddress = true) {
      this.newAppointment.contact_id = selectedContact.id;
      if (shouldSetAddress) {
        this.setAddressFromContact(selectedContact);
      }
    },
    setAddressFromClient(client) {
      if (client && client.Address) {
        this.newAppointment.address = client.Address + ' ' + (client.ZIPCode || '') + ' ' + (client.City || '') + ' ' + (client.County || '');
        this.newAppointment.latitude = client.Latitude || null;
        this.newAppointment.longitude = client.Longitude || null;
      }
    },
    setAddressFromContact(contact) {
      if (contact) {
        // Usa l'indirizzo già formattato dal controller o costruiscilo
        this.newAppointment.address = contact.full_address || (contact.Address + ' ' + (contact.ZIPCode || '') + ' ' + (contact.City || '') + ' ' + (contact.County || ''));
        this.newAppointment.latitude = contact.Latitude || null;
        this.newAppointment.longitude = contact.Longitude || null;
        
        // Pulisce i campi cliente/sede dato che stiamo usando un contatto
        this.newAppointment.company = '';
        this.newAppointment.branch_id = '';
        this.sites = [];
      }
    },
    setAddressFromSite() {
      const site = this.sites.find(site => site.id === this.newAppointment.branch_id);
      if (site && site.Address) {
        this.newAppointment.address = site.Address + ' ' + (site.ZIPCode || '') + ' ' + (site.City || '') + ' ' + (site.County || '');
        this.newAppointment.latitude = site.Latitude || null;
        this.newAppointment.longitude = site.Longitude || null;
      }
    },
    getSites(clientId) {
      axios.get(route('modules.contratti.sedi', clientId))
        .then((response) => {
          this.sites = response.data.map(item => {
            const company = item.CompanyName ? item.CompanyName + " - " : "";
            const name = `${company}${item.Address} ${item.ZIPCode} ${item.City} ${item.County}`;
            return {
              id: item.id,
              name: name,
              Address: item.Address,
              ZIPCode: item.ZIPCode,
              City: item.City,
              County: item.County,
              Latitude: item.Latitude,
              Longitude: item.Longitude
            };
          });
          if (this.newAppointment.branch_id) {
            this.setAddressFromSite();
          }
        }
      );
    },
    openFileDialog() {
      this.$refs.input && this.$refs.input.click()
    },
    onInputChange(e) {
      const newFiles = e.target.files
      this.handleFiles(newFiles)
      e.target.value = ''
    },
    onDrop(e) {
      const dropped = e.dataTransfer.files
      this.onDragLeave()
      this.handleFiles(dropped)
    },
    onDragEnter() {},
    onDragOver() {},
    onDragLeave() {},
    handleFiles(fileList) {

        this.currentAttachments = [...this.currentAttachments, ...fileList]
    },
    removeFile(f) {
      this.currentAttachments = this.currentAttachments.filter(x => x !== f)
    },
    emitFiles() {
      let _this = this;
      let packets = [];

      _this.readfileramaining = _this.currentAttachments.length;

      for (const file of this.currentAttachments) {
        let reader = new FileReader();

        reader.addEventListener("loadend", function (evt) { 
          packets.push( { name: file.name , data: new Uint8Array(evt.currentTarget.result).toBase64()}); 
          _this.readfileramaining--;
          if (_this.readfileramaining == 0){
            _this.concludeAppointment(packets);
          }
        });
        reader.readAsArrayBuffer(file)
      }
    },
    prettySize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      let size = bytes
      while (size >= 1024 && i < units.length - 1) {
        size = size / 1024
        i++
      }
      return `${Math.round(size * 10) / 10} ${units[i]}`
    },
    loadAttachments()
    {
      axios.post(route('modules.visite-commerciali.attachments'), { id: this.editingVisit.id})
        .then((response) => {
          this.attachmentLoaded = true;
          if (response.data.success){
            this.visitAttachments = response.data.attachments;        
          }
          else this.visitAttachments = [];
        }
      );
    }
  }
};
</script>